import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TakeSurveyPages from './TakeSurveyPages';
import { getShuffledOptions, getShuffledImageOptions } from '../../utils/shuffleOptions';
import {
  selectionHint,
  selectionProgressMessage,
  multiSelectBlocksProgress,
  isSelectionCapped,
} from '../../utils/multiSelectLimit';
import { matrixBlocksProgress, matrixProgressMessage, getMissingMatrixRows } from '../../utils/matrixCompletion';
import MatrixQuestion from '../../components/MatrixQuestion';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  LinearProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  TextField,
  Slider,
  Alert,
  Card,
  CardContent,
  FormGroup,
  FormControl,
  IconButton,
  Select,
  MenuItem
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function TakeSurveyEnhanced() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [usePagesMode, setUsePagesMode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSurveyMode();
  }, [id]);

  const checkSurveyMode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // External surveys should not be taken - redirect back to details
      if (response.data.isExternalSurvey) {
        navigate(`/respondent/surveys/${id}`, { replace: true });
        return;
      }

      // Check if survey has pages structure
      const hasPagesStructure = response.data.pages && response.data.pages.length > 0;
      setUsePagesMode(hasPagesStructure);
    } catch (err) {
      console.error('Error checking survey mode:', err);
      setUsePagesMode(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography>Загрузка...</Typography>
        </Paper>
      </Container>
    );
  }

  // Use page-based component if survey has pages
  if (usePagesMode) {
    return <TakeSurveyPages />;
  }

  // Otherwise use legacy component
  return <TakeSurveyEnhancedLegacy />;
}

// Legacy component (old logic-based flow)
function TakeSurveyEnhancedLegacy() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // pageStartIndex указывает на индекс первого вопроса текущей страницы внутри динамического потока
  const [pageStartIndex, setPageStartIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({}); // Store actual file objects for upload
  const [startTime] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [questionFlow, setQuestionFlow] = useState([]); // Track which questions to show based on logic
  const [pageSize, setPageSize] = useState(1); // 1 или 5 вопросов на странице (перенесено на уровень опроса)
  const [pageStack, setPageStack] = useState([0]); // История начала страниц для кнопки Назад
  const [attemptedNext, setAttemptedNext] = useState(false); // Флаг попытки нажатия "Далее"

  useEffect(() => {
    fetchSurvey();
  }, [id]);

  useEffect(() => {
    if (survey && survey.questions && survey.questions.length > 0) {
      // Initialize question flow
      setQuestionFlow([survey.questions[0].id]);
      setPageStartIndex(0);
      setPageStack([0]);
    }
  }, [survey]);

  const fetchSurvey = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurvey(response.data);
      setError('');
    } catch (err) {
      setError('Ошибка загрузки опроса');
      setSurvey(null);
    } finally {
      setLoading(false);
    }
  };

  const buildVisibleQuestions = () => {
    if (!survey || !survey.questions || questionFlow.length === 0) return { list: [], lastNext: null, pendingFlow: questionFlow };
    const result = [];
    let idx = pageStartIndex;
    let lastNext = null;
    let safety = 0;
    const localFlow = [...questionFlow];
    while (result.length < pageSize && idx < localFlow.length && safety < 200) {
      safety++;
      const qid = localFlow[idx];
      const q = findQuestionById(qid);
      if (!q) break;
      result.push(q);
      const nextId = getNextQuestionIdByLogic(qid);
      lastNext = nextId;
      if (!nextId || nextId === 'END') break;
      const existingIndex = localFlow.indexOf(nextId);
      if (existingIndex === -1) {
        localFlow.push(nextId);
        idx = localFlow.length - 1;
      } else {
        idx = existingIndex;
      }
    }
    return { list: result, lastNext, pendingFlow: localFlow };
  };

  const findQuestionById = (qid) => survey?.questions?.find(q => q.id === qid) || null;

  const getSequentialNextId = (qid) => {
    if (!survey || !survey.questions) return null;
    const idx = survey.questions.findIndex(q => q.id === qid);
    if (idx === -1) return null;
    if (idx < survey.questions.length - 1) return survey.questions[idx + 1].id;
    return null;
  };

  const getNextQuestionIdByLogic = (qid) => {
    const answer = answers[qid];
    const result = applyLogic(qid, answer);
    if (!result) return getSequentialNextId(qid);
    if (result.action === 'end') return 'END';
    if (result.action === 'endWithoutPayment') return { type: 'DISQUALIFIED', reason: result.reason || null };
    if (result.action === 'show' && result.target) return result.target;
    if (result.action === 'skip' && result.target) {
      const idx = survey.questions.findIndex(q => q.id === result.target);
      if (idx !== -1 && idx < survey.questions.length - 1) return survey.questions[idx + 1].id;
      return 'END';
    }
    return getSequentialNextId(qid);
  };

  const applyLogic = (questionId, answer) => {
    // Do not apply logic if no answer provided yet
    if (answer === undefined || answer === null ||
        (typeof answer === 'string' && answer.trim() === '') ||
        (Array.isArray(answer) && answer.length === 0)) {
      console.log('[Logic] Skipping logic evaluation: no answer yet');
      return null;
    }
    if (!survey.logic || survey.logic.length === 0) return null;

    // Find logic rules for this question
    const rules = survey.logic.filter(l => l.questionId === questionId);
    
    console.log(`[Logic] Checking ${rules.length} rules for question ${questionId}`);
      console.log(`[Logic] All logic rules in survey:`, survey.logic);
      console.log(`[Logic] Filtered rules for this question:`, rules);
    console.log(`[Logic] User answer:`, answer);
      console.log(`[Logic] User answer type:`, typeof answer, Array.isArray(answer) ? 'array' : 'not array');
    
    for (const rule of rules) {
      const condition = rule.condition;
      
      // Normalize condition answers to array (support both old 'answer' and new 'answers' format)
        const condAnswersRaw = Array.isArray(condition?.answers) && condition.answers.length > 0
          ? condition.answers
          : (condition?.answer ? [condition.answer] : []);
        // Normalize to string for robust comparison (e.g., 1 vs "1")
        const condAnswers = condAnswersRaw.map(a => (a === null || a === undefined) ? '' : String(a).trim());

      console.log(`[Logic] Rule condition answers:`, condAnswers, 'Action:', condition.action, 'Target:', condition.target);
        console.log(`[Logic] Condition answers types:`, condAnswers.map(a => typeof a));

      let conditionMet = false;

      // Check if answer matches condition (supports single or multiple answers)
        if (typeof answer === 'string') {
          const ans = String(answer).trim();
          console.log(`[Logic] Checking string answer "${ans}" against:`, condAnswers);
          conditionMet = condAnswers.includes(ans);
          console.log(`[Logic] String match result:`, conditionMet);
        } else if (Array.isArray(answer) && answer.length > 0) {
          const ansArr = answer.map(a => (a === null || a === undefined) ? '' : String(a).trim());
          console.log(`[Logic] Checking array answer`, ansArr, 'against:', condAnswers);
          conditionMet = ansArr.some(a => condAnswers.includes(a));
          console.log(`[Logic] Array match result:`, conditionMet);
      }

      console.log(`[Logic] Condition met:`, conditionMet);

      if (conditionMet) {
        console.log(`[Logic] Applying action: ${condition.action} to target: ${condition.target}`);
        return { action: condition.action, target: condition.target, reason: rule.disqualificationReason || null };
      }
    }

    console.log('[Logic] No matching rule found, using default flow');
    return null;
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = async () => {
    setError('');
    setAttemptedNext(true);
    // Собираем вопросы текущей страницы
    const { list: visible, lastNext, pendingFlow } = buildVisibleQuestions();
    if (!visible || visible.length === 0) return;

    // Валидация обязательных: проверяем, но не показываем общий баннер
    const hasUnanswered = visible.some((q) => {
      if (multiSelectBlocksProgress(q, answers[q.id])) return true;
      if (matrixBlocksProgress(q, answers[q.id])) return true;
      return q.required && (answers[q.id] === undefined || answers[q.id] === null || (typeof answers[q.id] === 'string' && answers[q.id].trim() === '') || (Array.isArray(answers[q.id]) && answers[q.id].length === 0));
    });
    if (hasUnanswered) {
      // Не показываем общую ошибку, пользователь увидит предупреждения под каждым вопросом
      return;
    }

    // Если логика для последнего вопроса страницы завершает опрос
    if (lastNext === 'END') {
      handleSubmit();
      return;
    }

    // Проверка на дисквалификацию
    if (lastNext && typeof lastNext === 'object' && lastNext.type === 'DISQUALIFIED') {
      await handleSubmitDisqualified(lastNext.reason);
      return;
    }

    // Вычисляем новый стартовый индекс для следующей страницы
    if (visible.length > 0) {
      const lastId = visible[visible.length - 1].id;
      const nextId = getNextQuestionIdByLogic(lastId);
      if (!nextId || nextId === 'END') {
        handleSubmit();
        return;
      }
      // Обновим поток, если локально добавили новые вопросы
      if (pendingFlow.length !== questionFlow.length) {
        setQuestionFlow(pendingFlow);
      }
      const existingIndex = pendingFlow.indexOf(nextId);
      if (existingIndex !== -1) {
        setPageStartIndex(existingIndex);
        setPageStack(prev => [...prev, existingIndex]);
        setAttemptedNext(false);
      } else {
        // Добавим и перейдем к нему
        const updated = [...pendingFlow, nextId];
        setQuestionFlow(updated);
        const newIndex = updated.length - 1;
        setPageStartIndex(newIndex);
        setPageStack(ps => [...ps, newIndex]);
        setAttemptedNext(false);
      }
    }
  };

  const handleBack = () => {
    setError('');
    setSubmitting(false);
    setAttemptedNext(false);
    setFiles(files => files);
    if (pageStack.length > 1) {
      const newStack = [...pageStack];
      newStack.pop();
      const newStart = newStack[newStack.length - 1] || 0;
      setPageStack(newStack);
      setPageStartIndex(newStart);
    }
  };

  const handleSubmitDisqualified = async (reason) => {
    try {
      setSubmitting(true);
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      const token = localStorage.getItem('token');

      const answersWithFiles = { ...answers };
      
      for (const [questionId, file] of Object.entries(files)) {
        if (file) {
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            const uploadResponse = await axios.post(`${API_BASE}/upload/survey-response`, formData, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            });
            
            answersWithFiles[questionId] = {
              fileUrl: uploadResponse.data.fileUrl,
              originalName: uploadResponse.data.originalName,
              size: uploadResponse.data.size,
              mimetype: uploadResponse.data.mimetype
            };
          } catch (uploadErr) {
            console.error('Error uploading file:', uploadErr);
          }
        }
      }
      
      await axios.post(`${API_BASE}/surveys/${id}/responses`, {
        answers: answersWithFiles,
        timeSpent,
        disqualified: true,
        disqualificationReason: reason || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate(`/respondent/surveys/${id}/disqualified`, {
        state: { reason: reason || null }
      });
    } catch (err) {
      console.error('Error submitting disqualified survey:', err);
      navigate(`/respondent/surveys/${id}/disqualified`, {
        state: { reason: reason || null }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      const token = localStorage.getItem('token');

      // Сначала загружаем все файлы и получаем их URLs
      const answersWithFiles = { ...answers };
      
      for (const [questionId, file] of Object.entries(files)) {
        if (file) {
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            const uploadResponse = await axios.post(`${API_BASE}/upload/survey-response`, formData, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            });
            
            // Сохраняем URL файла и оригинальное имя в ответах
            answersWithFiles[questionId] = {
              fileUrl: uploadResponse.data.fileUrl,
              originalName: uploadResponse.data.originalName,
              size: uploadResponse.data.size,
              mimetype: uploadResponse.data.mimetype
            };
          } catch (uploadErr) {
            console.error('Error uploading file:', uploadErr);
            setError(`Ошибка загрузки файла: ${file.name}`);
            return;
          }
        }
      }
      
      const submitRes = await axios.post(`${API_BASE}/surveys/${id}/responses`, {
        answers: answersWithFiles,
        timeSpent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const isProfile = submitRes.data?.isProfileSurvey;
      if (isProfile) {
        // Для профильного опроса обновим локально список завершённых без перехода в историю
        navigate('/respondent/profile', {
          state: { message: 'Профильный опрос завершён. Данные профиля обновлены.' }
        });
        return;
      }

      navigate('/respondent/history', {
        state: { message: 'Спасибо! Ваш ответ принят и вознаграждение начислено.' }
      });
    } catch (err) {
      console.error('Error submitting survey:', err);
      setError(err.response?.data?.error || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestionInput = (question) => {
    if (!question) return null;

    const answer = answers[question.id];

    const SERVER_BASE = API_BASE.replace('/api', '');

    switch (question.type) {
      case 'single':
        const customSingleKey = '__custom__';
        const isCustomSingle = answer && !question.options.includes(answer);
        const displayValueSingle = isCustomSingle ? customSingleKey : (answer || '');
        
        return (
          <Box>
            <RadioGroup 
              value={displayValueSingle} 
              onChange={(e) => {
                if (e.target.value === customSingleKey) {
                  handleAnswerChange(question.id, '');
                } else {
                  handleAnswerChange(question.id, e.target.value);
                }
              }}
            >
              {getShuffledOptions(question, id).map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
              {question.allowCustomOption && (
                <FormControlLabel
                  value={customSingleKey}
                  control={<Radio />}
                  label="Свой вариант"
                />
              )}
            </RadioGroup>
            {question.allowCustomOption && (displayValueSingle === customSingleKey || isCustomSingle) && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={isCustomSingle ? answer : ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                sx={{ mt: 1, pl: 4, boxSizing: 'border-box' }}
              />
            )}
          </Box>
        );

      case 'multiple':
        const customMultipleKey = '__custom__';
        const answerArray = Array.isArray(answer) ? answer : [];
        const customValues = answerArray.filter(a => !question.options.includes(a));
        const hasCustom = customValues.length > 0;
        const atSelectionCap = isSelectionCapped(question, answerArray);
        return (
          <Box>
            <FormGroup>
              {getShuffledOptions(question, id).map((option, index) => {
                const optionChecked = answerArray.includes(option);
                return (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={optionChecked}
                      disabled={!optionChecked && atSelectionCap}
                      onChange={(e) => {
                        if (e.target.checked && isSelectionCapped(question, answerArray)) return;
                        const newAnswer = [...answerArray.filter(a => question.options.includes(a) || a === customValues[0])];
                        if (e.target.checked) {
                          newAnswer.push(option);
                        } else {
                          const idx = newAnswer.indexOf(option);
                          if (idx > -1) newAnswer.splice(idx, 1);
                        }
                        handleAnswerChange(question.id, newAnswer);
                      }}
                    />
                  }
                  label={option}
                />
                );
              })}
              {question.allowCustomOption && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasCustom}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          // Убираем кастомное значение
                          const newAnswer = answerArray.filter(a => question.options.includes(a));
                          handleAnswerChange(question.id, newAnswer);
                        }
                      }}
                    />
                  }
                  label="Свой вариант"
                />
              )}
            </FormGroup>
            {question.allowCustomOption && hasCustom && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={customValues[0] || ''}
                onChange={(e) => {
                  const newAnswer = [...answerArray.filter(a => question.options.includes(a))];
                  if (e.target.value.trim()) {
                    newAnswer.push(e.target.value);
                  }
                  handleAnswerChange(question.id, newAnswer);
                }}
                sx={{ mt: 1, pl: 4, boxSizing: 'border-box' }}
              />
            )}
          </Box>
        );

      case 'multiple-image':
        const imageOptionsMulti = question.imageOptions || [];
        const answerArrayImg = Array.isArray(answer) ? answer : [];
        const customValuesImg = answerArrayImg.filter(a => !imageOptionsMulti.find(opt => opt.text === a));
        const hasCustomImg = customValuesImg.length > 0;
        const atImageSelectionCap = isSelectionCapped(question, answerArrayImg);
        
        return (
          <Box>
            <FormGroup>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                {getShuffledImageOptions(question, id).map((option, index) => {
                  const isChecked = answerArrayImg.includes(option.text);
                  const imageBlocked = !isChecked && atImageSelectionCap;
                  return (
                    <Paper
                      key={index}
                      elevation={isChecked ? 8 : 2}
                      sx={{
                        p: 2,
                        cursor: imageBlocked ? 'not-allowed' : 'pointer',
                        opacity: imageBlocked ? 0.55 : 1,
                        border: isChecked ? '2px solid primary.main' : 'none',
                        transition: 'all 0.3s'
                      }}
                      onClick={() => {
                        if (imageBlocked) return;
                        const validAnswers = answerArrayImg.filter(a => imageOptionsMulti.find(opt => opt.text === a) || a === customValuesImg[0]);
                        const newAnswer = [...validAnswers];
                        if (isChecked) {
                          const idx = newAnswer.indexOf(option.text);
                          if (idx > -1) newAnswer.splice(idx, 1);
                        } else {
                          newAnswer.push(option.text);
                        }
                        handleAnswerChange(question.id, newAnswer);
                      }}
                    >
                      {option.imageUrl && (
                        <Box sx={{ mb: 1 }}>
                          <img
                            src={option.imageUrl.startsWith('/') ? `${SERVER_BASE}${option.imageUrl}` : option.imageUrl}
                            alt={option.text}
                            style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px' }}
                          />
                        </Box>
                      )}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked}
                            disabled={imageBlocked}
                            onChange={(e) => {
                              if (e.target.checked && isSelectionCapped(question, answerArrayImg)) return;
                              const validAnswers = answerArrayImg.filter(a => imageOptionsMulti.find(opt => opt.text === a) || a === customValuesImg[0]);
                              const newAnswer = [...validAnswers];
                              if (e.target.checked) {
                                newAnswer.push(option.text);
                              } else {
                                const idx = newAnswer.indexOf(option.text);
                                if (idx > -1) newAnswer.splice(idx, 1);
                              }
                              handleAnswerChange(question.id, newAnswer);
                            }}
                          />
                        }
                        label={option.text}
                      />
                    </Paper>
                  );
                })}
              </Box>
              {question.allowCustomOption && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasCustomImg}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          const newAnswer = answerArrayImg.filter(a => imageOptionsMulti.find(opt => opt.text === a));
                          handleAnswerChange(question.id, newAnswer);
                        }
                      }}
                    />
                  }
                  label="Свой вариант"
                  sx={{ mt: 2 }}
                />
              )}
            </FormGroup>
            {question.allowCustomOption && hasCustomImg && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={customValuesImg[0] || ''}
                onChange={(e) => {
                  const validAnswers = answerArrayImg.filter(a => imageOptionsMulti.find(opt => opt.text === a));
                  const newAnswer = [...validAnswers];
                  if (e.target.value.trim()) {
                    newAnswer.push(e.target.value);
                  }
                  handleAnswerChange(question.id, newAnswer);
                }}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        );

      case 'text':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={answer || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder="Введите ваш ответ"
          />
        );

      case 'scale':
        return (
          <Box sx={{ px: 2 }}>
            <Slider
              value={typeof answer === 'number' ? answer : (typeof question.scaleMin === 'number' ? question.scaleMin : 0)}
              onChange={(e, newValue) => handleAnswerChange(question.id, newValue)}
              min={typeof question.scaleMin === 'number' ? question.scaleMin : 0}
              max={typeof question.scaleMax === 'number' ? question.scaleMax : 10}
              marks
              step={1}
              valueLabelDisplay="on"
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption">{typeof question.scaleMin === 'number' ? question.scaleMin : 0}</Typography>
              <Typography variant="caption">{typeof question.scaleMax === 'number' ? question.scaleMax : 10}</Typography>
            </Box>
          </Box>
        );

      case 'dropdown':
        const customDropdownKey = '__custom__';
        const isCustomDropdown = answer && !question.options.includes(answer);
        const displayValueDropdown = isCustomDropdown ? customDropdownKey : (answer || '');

        return (
          <Box>
            <FormControl fullWidth>
              <Typography variant="body2" sx={{ mb: 1 }}>Выберите один вариант</Typography>
              <Select
                value={displayValueDropdown}
                onChange={(e) => {
                  if (e.target.value === customDropdownKey) {
                    handleAnswerChange(question.id, '');
                  } else {
                    handleAnswerChange(question.id, e.target.value);
                  }
                }}
                displayEmpty
                size="small"
              >
                <MenuItem value="" disabled>
                  <em>Выберите...</em>
                </MenuItem>
                {getShuffledOptions(question, id).map((opt, idx) => (
                  <MenuItem key={idx} value={opt}>{opt}</MenuItem>
                ))}
                {question.allowCustomOption && (
                  <MenuItem value={customDropdownKey}>Свой вариант</MenuItem>
                )}
              </Select>
            </FormControl>
            {question.allowCustomOption && (displayValueDropdown === customDropdownKey || isCustomDropdown) && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={isCustomDropdown ? answer : ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        );

      case 'ranking':
        return (
          <Box>
            <Typography variant="body2" sx={{ mb:1 }}>Упорядочите варианты (перемещайте вверх/вниз)</Typography>
            {(() => {
              const current = Array.isArray(answer) && answer.length === (question.options || []).length
                ? answer
                : (question.options || []);
              const move = (idx, dir) => {
                const copy = [...current];
                const ni = dir === 'up' ? idx - 1 : idx + 1;
                if (ni < 0 || ni >= copy.length) return;
                [copy[idx], copy[ni]] = [copy[ni], copy[idx]];
                handleAnswerChange(question.id, copy);
              };
              return current.map((opt, idx) => (
                <Box key={opt+idx} sx={{ display:'flex', alignItems:'center', mb:1, gap:1 }}>
                  <Paper sx={{ flex:1, p:1 }} variant="outlined">{idx+1}. {opt}</Paper>
                  <IconButton size="small" onClick={() => move(idx,'up')} disabled={idx===0}>↑</IconButton>
                  <IconButton size="small" onClick={() => move(idx,'down')} disabled={idx===current.length-1}>↓</IconButton>
                </Box>
              ));
            })()}
          </Box>
        );

      case 'matrix':
        return (
          <MatrixQuestion
            rows={question.matrixRows || []}
            columns={question.matrixCols || []}
            value={answer || {}}
            missingRows={attemptedNext && matrixBlocksProgress(question, answer) ? getMissingMatrixRows(question, answer) : []}
            onChange={(next) => handleAnswerChange(question.id, next)}
          />
        );

      case 'likert':
        return (
          <RadioGroup value={answer || ''} onChange={(e)=>handleAnswerChange(question.id, e.target.value)}>
            {(question.likertOptions || [
              'Полностью согласен',
              'Скорее согласен',
              'Нейтрально',
              'Скорее не согласен',
              'Полностью не согласен'
            ]).map((opt, idx)=>(
              <FormControlLabel key={idx} value={opt} control={<Radio />} label={opt} />
            ))}
          </RadioGroup>
        );

      case 'nps':
        return (
          <Box sx={{ px:2 }}>
            <Typography variant="body2" sx={{ mb:1 }}>Насколько вероятно, что вы порекомендуете? (0-10)</Typography>
            <Slider
              value={typeof answer === 'number' ? answer : 0}
              onChange={(e, v) => handleAnswerChange(question.id, v)}
              min={0}
              max={10}
              marks
              step={1}
              valueLabelDisplay="on"
            />
            <Box sx={{ display:'flex', justifyContent:'space-between', mt:1 }}>
              <Typography variant="caption">0 (не буду)</Typography>
              <Typography variant="caption">10 (обязательно)</Typography>
            </Box>
          </Box>
        );

      case 'file':
        return (
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
            >
              Загрузить файл
              <input
                type="file"
                hidden
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    // Сохраняем файл для последующей загрузки
                    setFiles(prev => ({ ...prev, [question.id]: file }));
                    // Обновляем ответ с информацией о файле для отображения
                    handleAnswerChange(question.id, { fileName: file.name, fileSize: file.size });
                  }
                }}
              />
            </Button>
            {answer && answer.fileName && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Файл: {answer.fileName} ({(answer.fileSize / 1024).toFixed(2)} KB)
              </Typography>
            )}
          </Box>
        );

      default:
        return <Typography color="error">Неизвестный тип вопроса</Typography>;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography align="center" sx={{ mt: 2 }}>Загрузка опроса...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/respondent/catalog')} sx={{ mt: 2 }}>
          Вернуться к каталогу
        </Button>
      </Container>
    );
  }

  if (!survey || !survey.questions || survey.questions.length === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">Опрос не найден или не содержит вопросов</Alert>
        <Button onClick={() => navigate('/respondent/catalog')} sx={{ mt: 2 }}>
          Вернуться к каталогу
        </Button>
      </Container>
    );
  }

  const { list: visibleQuestions, lastNext } = buildVisibleQuestions();
  const totalCount = survey?.questions?.length || 0;
  const answeredCount = Object.keys(answers || {}).length;
  const progress = totalCount > 0 ? Math.min(100, Math.round((answeredCount / totalCount) * 100)) : 0;
  const isLastPage = !lastNext || lastNext === 'END';

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {survey.title}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Typography variant="body2">{Math.round(progress)}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} />
        </Box>

        {(visibleQuestions || []).map((q) => {
          const answer = answers[q.id];
          const rangeMessage = selectionProgressMessage(q, answer) || matrixProgressMessage(q, answer);
          const hint = selectionHint(q);
          const isEmpty = q.required && (answer === undefined || answer === null || (typeof answer === 'string' && answer.trim() === '') || (Array.isArray(answer) && answer.length === 0));
          const showError = attemptedNext && (isEmpty || multiSelectBlocksProgress(q, answer) || matrixBlocksProgress(q, answer));
          return (
            <Card key={q.id} variant="outlined" sx={{ mb: 3, borderColor: showError ? 'error.main' : undefined }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {q.text}
                  {q.required && (
                    <Typography component="span" color="error"> *</Typography>
                  )}
                </Typography>
                {hint && (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                    {hint}
                  </Typography>
                )}
                {renderQuestionInput(q)}
                {showError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {rangeMessage || 'Этот вопрос обязателен для ответа'}
                  </Alert>
                )}
              </CardContent>
            </Card>
          );
        })}

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={handleBack}
            disabled={pageStack.length <= 1}
          >
            Назад
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={submitting}
          >
            {submitting ? 'Отправка...' : isLastPage ? 'Завершить' : 'Далее'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
